import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import type {
  PopupDisplayConfig,
  PopupUserInfo,
} from "@/features/user-profile/types";
import type {
  FallbackUserSource,
  SendMessageOpenOptions,
} from "@/hooks/useSendMessageToUser";
import { useUserById } from "@/hooks/useUser";
import { isSameUserId } from "@/lib/utils/userId";
import {
  activePopupUserIdAtom,
  activePopupTriggerIdAtom,
  messageLoadingUsersAtom,
  popupPositionAtom,
  setMessageLoadingAtom,
} from "@/stores/userPopupState";

interface UseUserProfilePopupOptions {
  onSendMessage?: (
    userId: number,
    fallbackSource?: FallbackUserSource,
    openOptions?: SendMessageOpenOptions,
  ) => void | Promise<void>;
  currentUserId?: number | string;
  config?: Partial<PopupDisplayConfig>;
}

export const useUserProfilePopup = (
  options: UseUserProfilePopupOptions = {},
) => {
  const { onSendMessage, currentUserId, config = {} } = options;

  // グローバル状態
  const [activePopupState, setActivePopupState] = useAtom(
    activePopupUserIdAtom,
  );
  const [activePopupTriggerId, setActivePopupTriggerId] = useAtom(
    activePopupTriggerIdAtom,
  );
  const [popupPosition, setPopupPosition] = useAtom(popupPositionAtom);
  const loadingUsers = useAtomValue(messageLoadingUsersAtom);
  const [, setMessageLoading] = useAtom(setMessageLoadingAtom);

  const instanceId = useId();

  // Multiple popup wrappers can coexist (e.g. member list rows). We only
  // expose active state to the wrapper instance that opened it, so hover-leave
  // timers from another row cannot close the currently active popup.
  const activeUserId =
    activePopupState?.instanceId === instanceId
      ? activePopupState.userId
      : null;
  const activeTriggerId =
    activePopupState?.instanceId === instanceId ? activePopupTriggerId : null;

  // ユーザー情報取得
  const { data: activeUserData, isLoading: isUserDataLoading } =
    useUserById(activeUserId);

  // タイマー管理
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 設定のマージ
  const finalConfig = useMemo(
    () => ({
      trigger: "hover" as const,
      position: "auto" as const,
      usePortal: false,
      hoverDelay: 300,
      ...config,
    }),
    [config],
  );

  // ポップアップを表示
  const showPopup = useCallback(
    (userId: number, element?: HTMLElement, triggerId?: string) => {
      // 既存のタイマーをクリア
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      // 位置計算
      if (element && finalConfig.usePortal) {
        const rect = element.getBoundingClientRect();
        const newPosition = {
          top: rect.top + window.scrollY - 10,
          left: rect.right + window.scrollX + 5,
        };
        setPopupPosition(newPosition);
      }

      setActivePopupState({ userId, instanceId });
      setActivePopupTriggerId(triggerId ?? null);
    },
    [
      finalConfig,
      setActivePopupState,
      setActivePopupTriggerId,
      setPopupPosition,
      instanceId,
    ],
  );

  // ポップアップを隠す
  const hidePopup = useCallback(() => {
    if (finalConfig.trigger === "hover" && finalConfig.hoverDelay > 0) {
      hoverTimeoutRef.current = setTimeout(() => {
        setActivePopupState(null);
        setActivePopupTriggerId(null);
      }, finalConfig.hoverDelay);
    } else {
      setActivePopupState(null);
      setActivePopupTriggerId(null);
    }
  }, [finalConfig, setActivePopupState, setActivePopupTriggerId]);

  // ポップアップを即座に隠す
  const hidePopupImmediately = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setActivePopupState(null);
    setActivePopupTriggerId(null);
  }, [setActivePopupState, setActivePopupTriggerId]);

  // タイマーをキャンセル（ホバー継続時）
  const cancelHideTimer = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  // メッセージ送信処理
  const handleSendMessage = useCallback(
    async (userId: number) => {
      if (!onSendMessage) return;

      // 先にポップアップを閉じる
      hidePopupImmediately();

      // ローディング状態を設定
      setMessageLoading({ userId, isLoading: true });

      try {
        // onSendMessage を await で呼び出す（非同期の場合に対応）
        await Promise.resolve(
          onSendMessage(userId, undefined, {
            source: "popup",
            initialSearchMode: "participants",
            prefillTargetParticipant: true,
          }),
        );
      } catch (error) {
        console.error("Failed to send message:", error);
      } finally {
        setMessageLoading({ userId, isLoading: false });
      }
    },
    [onSendMessage, hidePopupImmediately, setMessageLoading],
  );

  // アクティブユーザー情報の構築
  const activeUserInfo: PopupUserInfo | null = activeUserId
    ? {
        id: activeUserId,
        name: activeUserData?.display_name || "",
        avatarUrl: activeUserData?.image_url || null,
      }
    : null;

  // ローディング状態
  const isLoading = loadingUsers.has(activeUserId || -1) || isUserDataLoading;

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // デバッグ用ログ（最適化）
  const prevActiveUserIdRef = useRef(activeUserId);
  const prevIsLoadingRef = useRef(isLoading);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 必要な場合のみ再実行
  useEffect(() => {
    if (
      prevActiveUserIdRef.current !== activeUserId ||
      prevIsLoadingRef.current !== isLoading
    ) {
      prevActiveUserIdRef.current = activeUserId;
      prevIsLoadingRef.current = isLoading;
    }
  }, [activeUserId, isLoading, finalConfig.trigger]);

  return {
    // 状態
    activeUserId,
    activeTriggerId,
    activeUserInfo,
    isLoading,
    popupPosition,

    // アクション
    showPopup,
    hidePopup,
    hidePopupImmediately,
    cancelHideTimer,
    handleSendMessage,

    // 設定
    config: finalConfig,

    // ヘルパー
    isCurrentUser: (userId: number) => isSameUserId(userId, currentUserId),
    isMessageLoading: (userId: number) => loadingUsers.has(userId),
  };
};
